import {
  useCallback,
  useContext,
  useEffect,
  useId,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type Ref,
} from 'react'
import { useIntl } from 'react-intl'

import { cn, focusRing, targetSize } from './cn'
import { markEdited } from './edited'
import { FieldError } from './FieldError'
import { FieldLabelContext } from './field-label'
import { Clock, X } from './icons'
import { setPendingEdit } from './pending-edits'
import { formatTime, minutesOf, parseTime, timeOf, type TimeOfDay } from './time-of-day'

const listHeight = 240
const viewportGutter = 16
/** Where the list opens while no time is set: the usual start of a working day. */
const startOfDay = 9 * 60

export interface TimePickerProps {
  /** The time, `HH:mm` (or `HH:mm:ss`, as some settings hold it), or empty for none. */
  readonly value: string
  /**
   * Called with the time as `HH:mm`, or empty once it is cleared, or when the text typed is not a
   * time, which the field then says (see `keepsInvalidText`). A value it holds with seconds is
   * left as it is until changed.
   */
  readonly onChange: (time: TimeOfDay) => void
  readonly onBlur?: () => void
  /** A caption above the field. Left out inside a `FormRow`, whose label then names the field. */
  readonly label?: string
  /** Keeps the label for assistive technology only. */
  readonly hideLabel?: boolean
  /** Follows the row's label when a `FormRow` holds several fields ("Start time" → "Start time After"). */
  readonly labelSuffix?: string
  /** The id of a hint that describes the field, read before any error. */
  readonly describedBy?: string | undefined
  /** The error message; when set, the stroke turns error and the field names the message. */
  readonly error?: string | undefined
  /** Adds a button that clears the time once one is set. */
  readonly clearable?: boolean
  /** The viewer may not change it: read-only, still focusable and readable, as TextField's `isLocked`. */
  readonly isLocked?: boolean
  /** The viewer's locale, which says whether times show on the 12- or 24-hour clock. */
  readonly locale?: string | undefined
  /**
   * Passes text that is not a time on as typed, rather than emptying the value, for a field where
   * empty means "none", so the form's own check refuses the save instead of dropping the time.
   */
  readonly keepsInvalidText?: boolean
  /** Minutes between the list's times; 15 by default. */
  readonly step?: number
  /** For a form library: the field's name and a ref that can focus it. */
  readonly name?: string
  readonly inputRef?: Ref<{ readonly focus: () => void }>
  /** Sizes the field; `w-36` by default. */
  readonly className?: string
}

/**
 * A time of day, matching `DatePicker` beside it: a field that takes a typed time, in the 12- or
 * 24-hour clock as the viewer's locale writes it, and opens a list of times in 15-minute steps.
 * The mockups have no time picker; this follows WAI-ARIA's editable combobox with a list.
 *
 * Focus stays in the field. Down or Up opens the list on the set time, or the nearest after it,
 * and moves through it; Page Down and Page Up move by an hour, Home and End to the first and last
 * time. Enter picks the time moved to, or takes the typed one, without submitting the form; Tab
 * takes it too and moves on.
 * Escape closes the list, and then puts back what the field held. Alt+Down opens the list without
 * moving through it. A typed time is taken as focus leaves or on Enter; until then, leaving the
 * page asks first, as for any unsaved change.
 */
export function TimePicker({
  value,
  onChange,
  onBlur,
  label,
  hideLabel = false,
  labelSuffix,
  describedBy,
  error,
  clearable = false,
  isLocked = false,
  locale,
  keepsInvalidText = false,
  step = 15,
  name,
  inputRef,
  className = 'w-36',
}: TimePickerProps) {
  const intl = useIntl()
  const inputId = useId()
  const labelId = useId()
  const suffixId = useId()
  const clearId = useId()
  const editId = useId()
  const listId = useId()
  const errorId = useId()
  const rowLabelId = useContext(FieldLabelContext)
  const input = useRef<HTMLInputElement>(null)
  const list = useRef<HTMLUListElement>(null)

  const [open, setOpen] = useState(false)
  // The option moved to, by index; -1 while none is.
  const [active, setActive] = useState(-1)
  // The text being typed; `null` while the field shows its value.
  const [draft, setDraft] = useState<string | null>(null)
  const draftRef = useRef<string | null>(null)
  // Text that is not a time, kept in the field with an error until it is changed.
  const [invalidText, setInvalidText] = useState<string | null>(null)
  const [position, setPosition] = useState<CSSProperties>({})

  const times = useMemo(() => Array.from({ length: Math.ceil((24 * 60) / step) }, (_, index) => index * step), [step])
  const labels = useMemo(() => times.map((minutes) => formatTime(minutes, locale)), [times, locale])
  const valueMinutes = minutesOf(value)

  // Text that is not a time empties the value, so a value set since, such as by a form reset, replaces it.
  if (invalidText !== null && value !== '') {
    setInvalidText(null)
  }

  useImperativeHandle(inputRef, () => ({ focus: () => input.current?.focus() }), [])

  const editDraft = useCallback((text: string | null) => {
    draftRef.current = text
    setDraft(text)
  }, [])

  const emit = useCallback(
    (time: TimeOfDay) => {
      markEdited(input.current)
      onChange(time)
    },
    [onChange],
  )

  // The nearest option at or after `minutes`, wrapping to the first.
  const nearest = useCallback((minutes: number) => {
    const index = times.findIndex((candidate) => candidate >= minutes)
    return index === -1 ? 0 : index
  }, [times])

  /** Takes the typed text: a time, empty, or text the field says is not a time. */
  const commit = useCallback(() => {
    const text = draftRef.current
    if (text === null) {
      return
    }
    editDraft(null)
    if (text.trim() === '') {
      setInvalidText(null)
      if (value !== '') {
        emit('')
      }
      return
    }
    const parsed = parseTime(text, locale)
    if (parsed === null && keepsInvalidText) {
      if (text.trim() !== value) {
        emit(text.trim())
      }
      return
    }
    if (parsed === null) {
      setInvalidText(text)
      if (value !== '') {
        emit('')
      }
      return
    }
    setInvalidText(null)
    // A value held with seconds is left alone while its minutes are unchanged.
    if (minutesOf(parsed) !== valueMinutes) {
      emit(parsed)
    }
  }, [editDraft, emit, locale, value, valueMinutes, keepsInvalidText])

  const pick = useCallback(
    (index: number) => {
      const minutes = times[index]
      if (minutes === undefined) {
        return
      }
      editDraft(null)
      setInvalidText(null)
      setOpen(false)
      setActive(-1)
      if (minutes !== valueMinutes) {
        emit(timeOf(minutes))
      }
    },
    [times, valueMinutes, editDraft, emit],
  )

  const place = useCallback(() => {
    const frame = input.current?.parentElement?.getBoundingClientRect()
    if (frame === undefined) {
      return
    }
    const width = Math.min(Math.max(frame.width, 160), window.innerWidth - 2 * viewportGutter)
    const left = Math.min(Math.max(frame.left, viewportGutter), window.innerWidth - viewportGutter - width)
    const above = Math.max(frame.top - 4 - viewportGutter, 0)
    const below = Math.max(window.innerHeight - frame.bottom - 4 - viewportGutter, 0)
    setPosition(
      below < Math.min(listHeight, 160) && above > below
        ? { bottom: window.innerHeight - frame.top + 4, left, width, maxHeight: Math.min(listHeight, above) }
        : { top: frame.bottom + 4, left, width, maxHeight: Math.min(listHeight, below) },
    )
  }, [])

  /** Opens the list, on the option `activeIndex`, or with none moved to when it is -1. */
  const openList = useCallback(
    (activeIndex: number) => {
      place()
      setActive(activeIndex)
      setOpen(true)
    },
    [place],
  )

  // Where the list starts: the typed time, the set time, or the start of the working day.
  const startIndex = useCallback(() => {
    const typed = draft === null ? null : parseTime(draft, locale)
    const minutes = typed === null ? valueMinutes : minutesOf(typed)
    return nearest(minutes ?? startOfDay)
  }, [draft, locale, valueMinutes, nearest])

  const close = useCallback(() => {
    setOpen(false)
    setActive(-1)
  }, [])

  // The list closes as the page scrolls or resizes, as DatePicker's popover does; not as it scrolls itself.
  useEffect(() => {
    if (!open) {
      return undefined
    }
    function onMove(event: Event) {
      if (event.type === 'scroll' && event.target instanceof Node && list.current?.contains(event.target) === true) {
        return
      }
      setOpen(false)
      setActive(-1)
    }
    window.addEventListener('scroll', onMove, true)
    window.addEventListener('resize', onMove)
    return () => {
      window.removeEventListener('scroll', onMove, true)
      window.removeEventListener('resize', onMove)
    }
  }, [open])

  // Keeps the option moved to, or the one the list opens near, in view, scrolling the list alone.
  const shown = active === -1 ? (open ? startIndex() : -1) : active
  useLayoutEffect(() => {
    const box = list.current
    const option = shown === -1 ? null : box?.children.item(shown)
    if (!open || box === null || !(option instanceof HTMLElement)) {
      return
    }
    const top = option.offsetTop
    const bottom = top + option.offsetHeight
    if (active === -1) {
      box.scrollTop = Math.max(top - (box.clientHeight - option.offsetHeight) / 2, 0)
    } else if (top < box.scrollTop) {
      box.scrollTop = top
    } else if (bottom > box.scrollTop + box.clientHeight) {
      box.scrollTop = bottom - box.clientHeight
    }
  }, [open, shown, active])

  const typed = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      editDraft(event.target.value)
      setInvalidText(null)
      if (!open) {
        openList(-1)
      } else {
        setActive(-1)
      }
    },
    [editDraft, open, openList],
  )

  const move = useCallback(
    (to: number) => {
      setActive(Math.min(Math.max(to, 0), times.length - 1))
    },
    [times.length],
  )

  const onKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLInputElement>) => {
      // Keys that finish an input method's word, such as Enter after 午後, are its own.
      if (event.nativeEvent.isComposing) {
        return
      }
      const hourSteps = Math.max(Math.round(60 / step), 1)
      switch (event.key) {
        case 'ArrowDown':
        case 'ArrowUp':
          event.preventDefault()
          if (event.altKey) {
            if (event.key === 'ArrowDown' && !open) {
              openList(-1)
            } else if (event.key === 'ArrowUp' && open) {
              close()
            }
          } else if (!open) {
            openList(startIndex())
          } else if (active === -1) {
            setActive(startIndex())
          } else {
            move(active + (event.key === 'ArrowDown' ? 1 : -1))
          }
          return
        case 'PageDown':
        case 'PageUp':
          if (open) {
            // Kept from scrolling the page, which would close the list.
            event.preventDefault()
            const from = active === -1 ? startIndex() : active
            move(from + (event.key === 'PageDown' ? hourSteps : -hourSteps))
          }
          return
        case 'Home':
        case 'End':
          // Only while an option is moved to; otherwise they move the caret in the text.
          if (open && active !== -1) {
            event.preventDefault()
            move(event.key === 'Home' ? 0 : times.length - 1)
          }
          return
        case 'Enter':
          if (open) {
            // Picking a time does not also submit the form.
            event.preventDefault()
            if (active === -1) {
              commit()
              close()
            } else {
              pick(active)
            }
          } else if (draftRef.current !== null) {
            // The person sees the time taken before a second Enter submits the form.
            event.preventDefault()
            commit()
          }
          return
        case 'Escape':
          if (open) {
            event.preventDefault()
            event.stopPropagation()
            close()
          } else if (draftRef.current !== null) {
            event.preventDefault()
            event.stopPropagation()
            editDraft(null)
          }
          return
        case 'Tab':
          if (open && active !== -1) {
            pick(active)
          }
          return
        default:
      }
    },
    [step, open, active, times.length, openList, close, startIndex, move, commit, pick, editDraft],
  )

  const leave = useCallback(() => {
    commit()
    close()
    onBlur?.()
  }, [commit, close, onBlur])

  const clickField = useCallback(() => {
    if (!open && !isLocked) {
      openList(-1)
    }
  }, [open, isLocked, openList])

  // The list and the clock button keep focus in the field as they are clicked.
  const keepFocus = useCallback((event: ReactMouseEvent) => event.preventDefault(), [])

  const toggle = useCallback(() => {
    input.current?.focus()
    if (open) {
      close()
    } else {
      openList(startIndex())
    }
  }, [open, close, openList, startIndex])

  const clear = useCallback(() => {
    editDraft(null)
    setInvalidText(null)
    close()
    emit('')
    input.current?.focus()
  }, [editDraft, close, emit])

  const example = formatTime(17 * 60 + 30, locale)
  // Text that is not a time: typed and emptied, or held as typed (`keepsInvalidText`).
  const isInvalid = invalidText !== null || (value !== '' && valueMinutes === null)
  const shownError = isInvalid ? intl.formatMessage({ id: 'ui.timePicker.invalid' }, { example }) : error
  const idleText = invalidText ?? (valueMinutes === null ? value : formatTime(valueMinutes, locale))
  const text = draft ?? idleText

  // Typed text the form has not seen yet: a tab closed or a page left now would lose it unasked.
  const isPending = draft !== null && draft !== idleText
  useEffect(() => {
    if (!isPending) {
      return undefined
    }
    setPendingEdit(editId, true)
    return () => setPendingEdit(editId, false)
  }, [editId, isPending])

  const hasLabel = label !== undefined && label !== ''
  const suffixed = !hasLabel && rowLabelId !== null && labelSuffix !== undefined && labelSuffix !== ''
  const nameIds = hasLabel ? labelId : rowLabelId === null ? undefined : suffixed ? `${rowLabelId} ${suffixId}` : rowLabelId
  const descriptions = [describedBy, shownError === undefined ? undefined : errorId].filter(Boolean).join(' ')
  const optionId = (index: number) => `${listId}-${String(index)}`

  return (
    <div className={cn('flex flex-col gap-1', className)}>
      {hasLabel && (
        <label id={labelId} htmlFor={inputId} className={hideLabel ? 'sr-only' : 'text-xs font-medium text-text-secondary'}>
          {label}
        </label>
      )}
      {suffixed && (
        <span id={suffixId} className="sr-only">
          {labelSuffix}
        </span>
      )}
      {/* The frame holds the field and its buttons side by side, as DatePicker's does (WCAG 2.5.8). */}
      <div
        className={cn(
          'flex min-h-10 w-full items-center rounded border has-[input:focus]:border-primary-main has-[input:focus]:ring-2 has-[input:focus]:ring-primary-main',
          isLocked ? 'bg-background-contrast-sm' : 'bg-background',
          shownError === undefined ? 'border-text-light' : 'border-error-main',
        )}
      >
        <input
          ref={input}
          id={inputId}
          name={name}
          type="text"
          value={text}
          onChange={typed}
          onKeyDown={isLocked ? undefined : onKeyDown}
          onBlur={leave}
          onClick={clickField}
          readOnly={isLocked}
          role={isLocked ? undefined : 'combobox'}
          aria-autocomplete={isLocked ? undefined : 'none'}
          aria-expanded={isLocked ? undefined : open}
          aria-controls={open ? listId : undefined}
          aria-activedescendant={open && active !== -1 ? optionId(active) : undefined}
          aria-labelledby={hasLabel ? undefined : nameIds}
          aria-invalid={shownError !== undefined || undefined}
          aria-describedby={descriptions === '' ? undefined : descriptions}
          autoComplete="off"
          spellCheck={false}
          className={cn(
            'h-10 w-full min-w-0 flex-1 rounded bg-transparent px-3 text-sm text-text-primary focus:outline-hidden',
            isLocked && 'cursor-default',
          )}
        />
        {clearable && !isLocked && value !== '' && (
          <button
            type="button"
            aria-labelledby={[clearId, nameIds].filter(Boolean).join(' ')}
            // Clears before the field's blur could take its text and remove this button.
            onMouseDown={keepFocus}
            onClick={clear}
            className={cn(
              targetSize,
              'shrink-0 cursor-pointer rounded text-text-light hover:bg-background-contrast-sm hover:text-text-secondary',
              focusRing,
            )}
          >
            <span id={clearId} hidden>
              {intl.formatMessage({ id: 'ui.timePicker.clear' })}
            </span>
            <X size={14} />
          </button>
        )}
        {!isLocked && (
          // Out of the Tab order, as WAI-ARIA's combobox has its button: the keys open the list from the field.
          <button
            type="button"
            tabIndex={-1}
            aria-label={intl.formatMessage({ id: 'ui.timePicker.showTimes' })}
            aria-expanded={open}
            onMouseDown={keepFocus}
            onClick={toggle}
            className={cn(targetSize, 'mr-2 shrink-0 cursor-pointer rounded text-text-light hover:bg-background-contrast-sm hover:text-text-secondary')}
          >
            <Clock size={16} />
          </button>
        )}
      </div>
      {shownError !== undefined && <FieldError id={errorId} message={shownError} />}

      {open && (
        // A select cannot hold a field to type in, so the list is WAI-ARIA's listbox, and a press on it
        // keeps focus in the field, as a combobox's does.
        // oxlint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role, jsx-a11y/prefer-tag-over-role
        <ul role="listbox"
          ref={list}
          id={listId}
          aria-labelledby={nameIds}
          onMouseDown={keepFocus}
          style={position}
          className="fixed z-50 m-0 list-none overflow-y-auto rounded border border-text-primary bg-background p-1 text-text-primary shadow-card-hover"
        >
          {labels.map((optionText, index) => (
            <TimeOption
              key={times[index]}
              id={optionId(index)}
              index={index}
              text={optionText}
              selected={times[index] === valueMinutes}
              active={index === active}
              onPick={pick}
            />
          ))}
        </ul>
      )}
    </div>
  )
}

function TimeOption(props: {
  readonly id: string
  readonly index: number
  readonly text: string
  readonly selected: boolean
  readonly active: boolean
  readonly onPick: (index: number) => void
}) {
  const { index, onPick } = props
  const pick = useCallback(() => onPick(index), [onPick, index])
  return (
    // The field takes the keys and names this option as the one moved to (aria-activedescendant).
    // oxlint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role, jsx-a11y/prefer-tag-over-role, jsx-a11y/click-events-have-key-events
    <li role="option"
      id={props.id}
      aria-selected={props.selected}
      onClick={pick}
      className={cn(
        'flex min-h-8 cursor-pointer items-center rounded px-3 text-sm',
        props.selected ? 'bg-primary-main text-primary-contrast' : 'hover:bg-background-contrast-sm',
        props.active && (props.selected ? 'outline-2 -outline-offset-4 outline-primary-contrast' : 'bg-background-contrast-sm outline-2 -outline-offset-2 outline-primary-main'),
      )}
    >
      {props.text}
    </li>
  )
}
